// Core Modules
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';

// External Modules
import { NgxPermissionsService } from 'ngx-permissions';
import { Observable, catchError, finalize, forkJoin, lastValueFrom, map, switchMap, take } from 'rxjs';

// Services
import { AccessControlService } from '@core/services/access-control/access-control.service';
import { AuthService } from '@core/services/auth/auth.service';
import { SessionManagerService } from '@core/services/session-manager/session-manager.service';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

/**
 * Permission Guard
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionGuardCanActivate {
  // Injectors
  private readonly accessControlService = inject(AccessControlService);
  private readonly authService = inject(AuthService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly router = inject(Router);
  private readonly sessionManager = inject(SessionManagerService);
  /**
   * method to validate activated route
   * @param next activate route
   * @returns permissions for route boolean: true/false
   */
  public canActivate(next: ActivatedRouteSnapshot): Observable<boolean> | Promise<boolean> {
    const includePermissions = (next.data?.permissions?.only || []) as Array<string>;
    const excludePermissions = (next.data?.permissions?.except || []) as Array<string>;
    const redirectTo = next.data?.permissions?.redirectTo;
    return lastValueFrom(this.getPermissions(includePermissions, excludePermissions, redirectTo));
  }

  /**
   * getAccessStatus method is to navigate to redirectTo url if correct permission doesn't exist and return false or return true
   * @param includePermissions include permissions array for which permission was validated (permissions only)
   * @param excludePermissions exclude permissions array for which permission was validated (permissions except)
   * @param hasIncludedPermissionsAccess whether access is there for included permission
   * @param hasExcludedPermissionsAccess whether access is there for excluded permission
   * @param redirectTo URL to be navigated to if correct permission is not present
   * @returns boolean whether true or false
   */
  private getAccessStatus(includePermissions: string[], excludePermissions: string[], hasIncludedPermissionsAccess: boolean, hasExcludedPermissionsAccess: boolean, redirectTo: string): boolean {
    // If includePermissions is available and permission is not present or If excludePermissions is available and permission is present - navigate to redirectTo and return false
    if ((includePermissions.length && !hasIncludedPermissionsAccess) || (excludePermissions.length && hasExcludedPermissionsAccess)) {
      this.router.navigateByUrl(redirectTo);
      return false;
    } else return true;
  }

  /**
   * Get the current user permission for app
   * @param routePermissions current route permission
   * @param redirectTo If fail redirect to this route
   * @returns
   */
  private getPermissions(includePermissions: string[], excludePermissions: string[], redirectTo: string): Observable<boolean> {
    this.authService.authenticationInProgressSubject.next(true);
    // If permission length is zero considering it as browser refresh and refreshing the users session, navigate to respective landing page
    if (Object.keys(this.ngxPermissionsService.getPermissions())?.length === 0) {
      console.log('<PermissionGuard> - <canActivate> - No permissions found, refreshing session');
      return this.authService.refreshSession().pipe(
        take(1),
        switchMap(() => {
          this.sessionManager.setupSessionEvents();
          return this.accessControlService.definePermissions().pipe(
            switchMap(() => forkJoin([this.ngxPermissionsService.hasPermission(includePermissions), this.ngxPermissionsService.hasPermission(excludePermissions)])),
            map((hasAccess: boolean[]) => {
              const [hasIncludedPermissions, hasExcludedPermissions] = hasAccess;
              return this.getAccessStatus(includePermissions, excludePermissions, hasIncludedPermissions, hasExcludedPermissions, redirectTo);
            })
          );
        }),
        catchError((error) => {
          console.log('<PermissionGuard> - <canActivate> - Failed while refreshing the session and navigating to specific page based on scope', error);
          this.sessionManager.stopWatching();
          this.authService.logout();
          return this.router.navigate([`/${MainNavigationEnum.LOGIN}`]);
        }),
        finalize(() => this.authService.authenticationInProgressSubject.next(false))
      );
    } else {
      return forkJoin([this.ngxPermissionsService.hasPermission(includePermissions), this.ngxPermissionsService.hasPermission(excludePermissions)]).pipe(
        map((hasAccess: boolean[]): boolean => {
          const [hasIncludedPermissions, hasExcludedPermissions] = hasAccess;
          return this.getAccessStatus(includePermissions, excludePermissions, hasIncludedPermissions, hasExcludedPermissions, redirectTo);
        }),
        finalize(() => this.authService.authenticationInProgressSubject.next(false))
      );
    }
  }
}

export const PermissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => inject(PermissionGuardCanActivate).canActivate(route);
